"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle,
  Clock,
  Truck,
  MapPinned,
  X,
  RotateCcw,
  CalendarDays,
  CreditCard,
  Wallet,
  Tag,
  Coins,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Helper function to format date
const formatDate = (date) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString("en-IN", options);
};

export default function CustomerOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [returnModal, setReturnModal] = useState(false);
  const emptyReturnData = {
    order_code: "",
    order_detail_id: "",
    product_name: "",
    type: "return",
    return_reason: "",
    customer_note: "",
    product_bundle_id: null,
    isBundle: false,
    bundleName: "",
    bundleItems: [],
    selectedItems: [],
    fullBundle: false,
  };
  const [returnData, setReturnData] = useState(emptyReturnData);
  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!authToken || !authUserId) {
      console.error("Auth token or userId not found in localStorage");
      navigate("/login");
    }
  }, [authToken, authUserId, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("User not logged in. Please login first.");
          setOrders([]);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          "https://tyka.premierhostings.com/beta/api/get-user-orders",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        console.log("response", response.data);

        if (response?.data && Array.isArray(response?.data?.data)) {
          setOrders(response.data.data);
        } else {
          setError("No orders found.");
          setOrders([]);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to fetch orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold">
            <Clock size={16} /> Pending
          </span>
        );
      case "shipped":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
            <Truck size={16} /> Shipped
          </span>
        );
      case "reached":
      case "delivered":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
            <CheckCircle size={16} /> Delivered
          </span>
        );
      case "track":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
            <MapPinned size={16} /> Tracking
          </span>
        );
      case "return":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold">
            <RotateCcw size={16} /> Returned
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-200 text-gray-700 text-sm font-semibold">
            {status
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase()) || "Unknown"}
          </span>
        );
    }
  };

  const handleReturnRequest = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const url = "https://tyka.premierhostings.com/beta/api/product-returns";

      // Build one request body per case
      let targets = [];

      console.log("returnData:", returnData);

      if (!returnData.isBundle) {
        // Individual product
        targets.push({
          order_detail_id: returnData.order_detail_id,
        });
      } else if (returnData.fullBundle) {
        // Full bundle
        targets.push({
          order_detail_id: returnData.order_detail_id,
          item_type: "bundle",
          product_bundle_id: returnData.product_bundle_id,
          product_bundle_item_id: null,
        });
      } else {
        // Selected bundle item(s)
        targets = returnData.selectedItems.map((id) => {
          const item = returnData.bundleItems.find((i) => i.id === id);

          return {
            order_detail_id: item.id,
            item_type: "bundle_item",
            product_bundle_id: returnData.product_bundle_id,
            product_bundle_item_id:
              item.product_bundle_item_id ??
              item.products?.bundles
                ?.find((bundle) => bundle.id === returnData.product_bundle_id)
                ?.products?.find(
                  (bundleProduct) =>
                    bundleProduct.product_id === item.product_id,
                )?.bundle_item_id ??
              null,
          };
        });
      }
      if (targets.length === 0 || !targets[0].order_detail_id) {
        Swal.fire({
          icon: "warning",
          title: "Select a product",
          text: "Please select the full bundle or at least one product.",
        });
        return;
      }

      if (!returnData.return_reason.trim()) {
        Swal.fire({ icon: "warning", title: "Please enter a return reason." });
        return;
      }

      const guestEmail = localStorage.getItem("authUserEmail"); // change key if yours differs

      const results = await Promise.allSettled(
        targets.map((t) =>
          axios.post(
            url,
            {
              order_code: returnData.order_code,
              ...t,
              return_reason: returnData.return_reason,
              customer_note: returnData.customer_note,
              request_type: returnData.type, // "return" | "exchange"
              ...(guestEmail ? { guest_email: guestEmail } : {}),
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );

      const failed = results.filter(
        (r) => r.status === "rejected" || !r.value?.data?.success,
      );

      if (failed.length === 0) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Request submitted successfully.",
        });
        setReturnModal(false);
        setReturnData(emptyReturnData);
      } else {
        const msg =
          failed[0].reason?.response?.data?.message ||
          failed[0].value?.data?.message ||
          "Some requests failed.";
        Swal.fire({
          icon: failed.length === targets.length ? "error" : "warning",
          title:
            failed.length === targets.length ? "Failed" : "Partially submitted",
          text: `${targets.length - failed.length} of ${targets.length} submitted. ${msg}`,
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.response?.data?.message || "Unable to submit request.",
      });
    }
  };

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      {/* Heading */}
      <h1 className="text-3xl font-bold mb-8 text-center">📦 My Orders</h1>

      {/* Loading State */}
      {loading && (
        <p className="text-center text-gray-600 ">Loading your orders...</p>
      )}

      {/* Error State */}
      {error && !loading && (
        <p className="text-center text-red-500 font-medium">{error}</p>
      )}

      {/* Orders List */}
      {!loading && !error && (
        <div className="space-y-6">
          {orders.length > 0 ? (
            orders.map((order) => {
              const groupedOrderDetails = Object.values(
                order.order_details.data.reduce((acc, item) => {
                  // Row has a bundle object → it is a bundle item
                  if (item.bundle) {
                    const bundleId = item.bundle.id;
                    const key = `bundle-${bundleId}`;

                    if (!acc[key]) {
                      acc[key] = {
                        id: key,
                        isBundle: true,
                        bundleId,
                        bundle: item.bundle,
                        items: [],
                      };
                    }

                    // Set the fields on the frontend
                    acc[key].items.push({
                      ...item,
                      item_type: "bundle_item",
                      product_bundle_id: bundleId,
                      product_bundle_item_id:
                        item.product_bundle_item_id ?? null, // fill in your own value here
                    });
                  } else {
                    // Normal product
                    acc[`product-${item.id}`] = {
                      ...item,
                      isBundle: false,
                      item_type: "product",
                    };
                  }

                  return acc;
                }, {}),
              );

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-5"
                >
                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b border-gray-200">
                    <div className="w-full">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Order #{order.order_code}
                      </h2>

                      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 text-sm sm:grid-cols-3 lg:grid-cols-3">
                        {/* Order Date */}
                        <div>
                          <p className="text-gray-500">Order Date</p>
                          <p className="mt-1 font-medium text-gray-800">
                            {formatDate(order.order_date)}
                          </p>
                        </div>

                        {/* Payment Status */}
                        <div>
                          <p className="text-gray-500">Payment Status</p>
                          <p className="mt-1 font-medium capitalize text-gray-800">
                            {order.payment_status || "-"}
                          </p>
                        </div>

                        {/* Payment Type */}
                        <div>
                          <p className="text-gray-500">Payment Type</p>
                          <p className="mt-1 font-medium uppercase text-gray-800">
                            {order.payment_type || "-"}
                          </p>
                        </div>

                        {/* Product Price */}
                        <div>
                          <p className="text-gray-500">Product Price</p>
                          <p className="mt-1 font-semibold text-gray-900">
                            ₹
                            {Number(order.total_amount || 0).toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </p>
                        </div>

                        {/* Shipping Charge */}
                        <div>
                          <p className="text-gray-500">Shipping Charge</p>
                          <p className="mt-1 font-semibold text-gray-900">
                            ₹
                            {Number(order.shipping_charge || 0).toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </p>
                        </div>

                        {/* Total Amount */}
                        <div>
                          <p className="text-gray-500">Total Amount</p>
                          <p className="mt-1 text-base font-bold text-gray-900">
                            ₹
                            {Number(
                              order.final_amount ?? order.total_amount ?? 0,
                            ).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 shrink-0 md:mt-0 md:ml-4">
                      {getStatusBadge(order.order_status)}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {groupedOrderDetails.map((orderDetail) => {
                      // Normal Product
                      if (!orderDetail.isBundle) {
                        return (
                          <div
                            key={orderDetail.id}
                            className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-gray-50 rounded-xl items-center"
                          >
                            {/* Product Image */}
                            <div className="md:col-span-2 flex justify-center">
                              <div className="w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                                <img
                                  src={
                                    orderDetail.products.default_image ||
                                    "/placeholder.svg"
                                  }
                                  alt={orderDetail.products.name}
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                            </div>

                            {/* Product Details */}
                            <div className="md:col-span-8">
                              <div className="flex items-center gap-2">
                                <h3 className="text-xl font-semibold text-gray-800">
                                  {orderDetail.products.name}
                                </h3>

                                <X size={14} className="text-gray-500" />

                                <span className="text-lg font-medium text-gray-700">
                                  {orderDetail.quantity}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3 text-sm">
                                {/* Color */}
                                <div>
                                  <p className="text-gray-500">Color</p>

                                  <div className="flex items-center gap-2 mt-1">
                                    {orderDetail.colors.hexa_code_2 ? (
                                      <div className="relative w-5 h-5 rounded-full border overflow-hidden">
                                        <div
                                          className="absolute left-0 top-0 w-1/2 h-full"
                                          style={{
                                            backgroundColor:
                                              orderDetail.colors.hexa_code,
                                          }}
                                        />
                                        <div
                                          className="absolute right-0 top-0 w-1/2 h-full"
                                          style={{
                                            backgroundColor:
                                              orderDetail.colors.hexa_code_2,
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <span
                                        className="w-5 h-5 rounded-full border"
                                        style={{
                                          backgroundColor:
                                            orderDetail.colors.hexa_code,
                                        }}
                                      />
                                    )}

                                    <span>{orderDetail.colors.name}</span>
                                  </div>
                                </div>

                                {/* Size */}
                                <div>
                                  <p className="text-gray-500">Size</p>
                                  <p className="font-medium">
                                    {orderDetail.sizes.name}
                                  </p>
                                </div>

                                {/* Price */}
                                <div>
                                  <p className="text-gray-500">Price</p>
                                  <p className="font-bold text-red-600">
                                    ₹
                                    {Number(orderDetail.price).toLocaleString(
                                      "en-IN",
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Status */}
                            <div className="md:col-span-2 flex justify-end gap-2">
                              <span className="inline-flex items-center justify-center text-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                                {orderDetail.status
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase(),
                                  )}
                              </span>

                              {![
                                "pending",
                                "processing",
                                "cancelled",
                                "return_requested",
                                "return_approved",
                                "return_rejected",
                                "item_returned",
                                "refund_initiated",
                                "refunded",
                                "exchange_requested",
                                "exchange_approved",
                                "exchange_completed",
                              ].includes(order.order_status) && (
                                <button
                                  onClick={() => {
                                    setReturnData({
                                      ...emptyReturnData,
                                      order_code: order.order_code,
                                      order_detail_id: orderDetail.id,
                                      product_name: orderDetail.products.name,
                                    });
                                    setReturnModal(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-2 rounded-full bg-orange-100 text-orange-700"
                                >
                                  <RotateCcw size={16} />
                                  Return
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Bundle
                      return (
                        <div className="border rounded-xl p-5 bg-gray-50">
                          {/* Bundle Header */}
                          <div className="flex justify-between items-center mb-5 border-b pb-3">
                            <div>
                              <h2 className="text-xl font-bold">
                                Bundle: {orderDetail.bundle.name} ({" "}
                                {orderDetail.isBundle} {orderDetail.id}{" "}
                                {orderDetail.items[0].status})
                              </h2>
                              <p className="text-sm text-gray-500">
                                Offer Price: ₹{orderDetail.bundle.offer_price}
                              </p>
                            </div>

                            <div>
                              {![
                                "pending",
                                "processing",
                                "cancelled",
                                "return_requested",
                                "return_approved",
                                "return_rejected",
                                "item_returned",
                                "refund_initiated",
                                "refunded",
                                "exchange_requested",
                                "exchange_approved",
                                "exchange_completed",
                              ].includes(order.order_status) && (
                                <button
                                  onClick={() => {
                                    setReturnData({
                                      ...emptyReturnData,
                                      order_code: order.order_code,
                                      order_detail_id: orderDetail.bundleId,
                                      product_name: orderDetail.bundle?.name,
                                      product_bundle_id: orderDetail.bundleId,
                                      isBundle: true,
                                      bundleName: orderDetail.bundle?.name,
                                      bundleItems: orderDetail.items,
                                    });

                                    setReturnModal(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-2 rounded-full bg-orange-100 text-orange-700"
                                >
                                  <RotateCcw size={16} />
                                  Return
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Bundle Products */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {orderDetail.items.map((item) => (
                              <div
                                key={item.id}
                                className="flex gap-4 rounded-lg p-4 bg-white"
                              >
                                {/* Image */}
                                <div className="w-24 h-24 flex-shrink-0 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                                  <img
                                    src={
                                      item.products?.default_image ||
                                      "/placeholder.svg"
                                    }
                                    alt={item.products?.name}
                                    className="w-full h-full object-contain"
                                  />
                                </div>

                                {/* Content */}
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-800">
                                    {item.products?.name}
                                  </h3>

                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 text-sm">
                                    <div>
                                      <p className="text-gray-500">Color</p>
                                      <p>{item.colors?.name}</p>
                                    </div>

                                    <div>
                                      <p className="text-gray-500">Size</p>
                                      <p>{item.sizes?.name}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500 pl-2">
                                        Status
                                      </p>
                                      <p>{getStatusBadge(item.status)}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-gray-600">You have no orders yet.</p>
          )}
        </div>
      )}
      {returnModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-xl font-bold">Return / Exchange</h2>
              <button onClick={() => setReturnModal(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Order Code</label>

                  <input
                    value={returnData.order_code}
                    readOnly
                    className="w-full border rounded-lg p-2 bg-gray-100"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Product</label>

                  {!returnData.isBundle ? (
                    <input
                      value={returnData.product_name}
                      readOnly
                      className="w-full border rounded-lg p-2 bg-gray-100"
                    />
                  ) : (
                    <div className="border rounded-lg p-3 space-y-3">
                      {/* Full Bundle */}
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={returnData.fullBundle}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setReturnData((prev) => ({
                              ...prev,
                              fullBundle: checked,
                              selectedItems: [],
                            }));
                          }}
                          className="w-4 h-4"
                        />

                        <div>
                          <p className="font-semibold text-gray-800">
                            Full Bundle
                          </p>

                          <p className="text-sm text-gray-500">
                            {returnData.bundleName}
                          </p>
                        </div>
                      </label>

                      {/* Bundle Products */}
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-2">
                          Or select product(s)
                        </p>

                        <div className="space-y-2">
                          {returnData.bundleItems.map((item) => {
                            const isSelected =
                              returnData.selectedItems.includes(item.id);

                            return (
                              <label
                                key={item.id}
                                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                                  isSelected ? "bg-red-50 border-red-300" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={returnData.fullBundle}
                                  onChange={(e) => {
                                    const checked = e.target.checked;

                                    setReturnData((prev) => ({
                                      ...prev,
                                      selectedItems: checked
                                        ? [...prev.selectedItems, item.id]
                                        : prev.selectedItems.filter(
                                            (id) => id !== item.id,
                                          ),
                                    }));
                                  }}
                                  className="w-4 h-4"
                                />

                                <img
                                  src={
                                    item.products?.default_image ||
                                    "/placeholder.svg"
                                  }
                                  alt={item.products?.name}
                                  className="w-14 h-14 object-contain rounded border"
                                />

                                <div className="flex-1">
                                  <p className="font-medium">
                                    {item.products?.name}
                                  </p>

                                  <p className="text-sm text-gray-500">
                                    Color: {item.colors?.name || "-"}
                                  </p>

                                  <p className="text-sm text-gray-500">
                                    Size: {item.sizes?.name || "-"}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium">Request Type</label>

                  <select
                    value={returnData.type}
                    onChange={(e) =>
                      setReturnData({
                        ...returnData,
                        type: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="return">Return</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium">Return Reason</label>

                  <input
                    value={returnData.return_reason}
                    onChange={(e) =>
                      setReturnData({
                        ...returnData,
                        return_reason: e.target.value,
                      })
                    }
                    placeholder="Size issue"
                    className="w-full border rounded-lg p-2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Customer Note</label>

                  <textarea
                    rows={4}
                    value={returnData.customer_note}
                    onChange={(e) =>
                      setReturnData({
                        ...returnData,
                        customer_note: e.target.value,
                      })
                    }
                    placeholder="Need another size..."
                    className="w-full border rounded-lg p-2"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-5 border-t">
              <button
                onClick={() => {
                  setReturnModal(false);
                  setReturnData(emptyReturnData);
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleReturnRequest}
                className="px-4 py-2 bg-red-600 text-white rounded-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
